import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Password reset email sent!');
    } catch (error) {
      // Don't reveal if email exists or not for security
      setEmailSent(true);
      toast.success('If an account exists with this email, you will receive a password reset link.');
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="text-[#881337]" size={32} fill="#881337" />
              <span className="text-3xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-serif font-bold text-[#1C1917] mb-4">Check Your Email</h2>
            
            <p className="text-[#57534E] mb-6">
              If an account exists with <strong>{email}</strong>, you will receive a password reset link within a few minutes.
            </p>
            
            <p className="text-sm text-[#78716C] mb-6">
              The link will expire in 15 minutes for security reasons.
            </p>

            <div className="space-y-4">
              <Button
                onClick={() => setEmailSent(false)}
                variant="outline"
                className="w-full rounded-full py-6 border-[#881337] text-[#881337] hover:bg-[#881337]/10"
              >
                Try a different email
              </Button>
              
              <Link
                to="/auth"
                className="block text-center text-[#881337] font-medium hover:underline"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="text-[#881337]" size={32} fill="#881337" />
            <span className="text-3xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
          <p className="text-[#57534E]">Reset your password</p>
        </div>

        <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-8">
          <button
            onClick={() => navigate('/auth')}
            className="flex items-center gap-2 text-[#57534E] hover:text-[#1C1917] mb-6 transition-colors"
          >
            <ArrowLeft size={18} />
            <span>Back to Login</span>
          </button>

          <div className="w-14 h-14 bg-[#881337]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-7 h-7 text-[#881337]" />
          </div>

          <h2 className="text-2xl font-serif font-bold text-[#1C1917] mb-2 text-center">Forgot Password?</h2>
          <p className="text-[#57534E] text-center mb-6 text-sm">
            No worries! Enter your email address and we'll send you a link to reset your password.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#1C1917] mb-2 block">Email Address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                required
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white transition-all duration-300"
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
